import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';

import { SortIcon } from '@/components/common/SortIcon';
import { DeleteDialog } from '@/components/common/Dialogs';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/features/auth';
import { SettingsAction } from './SettingsAction';
import { ContractPeriodAction } from './ContractPeriodAction';
import { MatterPracticeAreaAction } from './MatterPracticeAreaAction';
import { TaskTypeAction } from './TaskTypeAction';

interface BaseSettingsItem {
  id: number;
  name: string;
  createdAt: string;
  interval?: number;
  is_active?: boolean;
}

interface GetSettingsColumnsProps {
  orderBy: string | undefined;
  order: 'asc' | 'desc' | undefined;
  onSort: (field: string) => void;
  resource: string;
  apiEndpoint: string;
  queryKey: string;

  title: string;
  showInterval?: boolean;
}

export function useSettingsColumns<T extends BaseSettingsItem>({
  orderBy,
  order,
  onSort,
  resource,
  apiEndpoint,
  queryKey,
  title,
  showInterval = false,
}: GetSettingsColumnsProps): ColumnDef<T>[] {
  const { hasAccess } = useAuth();
  const { t } = useTranslation();

  const isContractPeriod = resource === 'contract-period';
  const isMatterPracticeArea = resource === 'matter-practice-area';
  const isTaskType = resource === 'task-type';

  const columns: ColumnDef<T>[] = [
    {
      accessorKey: 'name',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('name')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('name')}
        >
          <p>{t('name')}</p>
          <SortIcon active={orderBy === 'name'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.name}</div>,
    },
  ];

  // Add interval column if needed (for Contract Periods)
  if (showInterval) {
    columns.push({
      accessorKey: 'interval',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('interval')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('interval')}
        >
          <p>{t('interval')}</p>
          <SortIcon active={orderBy === 'interval'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.interval ?? '-'}</div>,
    });
  }

  // Add is_active column for Task Types
  if (isTaskType) {
    columns.push({
      accessorKey: 'is_active',
      header: () => <p>{t('isActive')}</p>,
      cell: ({ row }) => {
        const isActive = row.original.is_active;
        return (
          <Badge variant={isActive ? 'default' : 'secondary'}>
            {isActive ? t('active') : t('inactive')}
          </Badge>
        );
      },
    });
  }

  // Add createdAt column
  columns.push({
    accessorKey: 'createdAt',
    header: () => (
      <div
        className="flex items-center cursor-pointer select-none"
        role="button"
        tabIndex={0}
        onClick={() => onSort('createdAt')}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('createdAt')}
      >
        <p>{t('createdAt')}</p>
        <SortIcon active={orderBy === 'createdAt'} direction={order} />
      </div>
    ),
    cell: ({ row }) => {
      const dateStr = row?.original?.createdAt;
      return <div>{format(new Date(dateStr), 'yyyy-MM-dd HH:mm')}</div>;
    },
  });

  // Add actions column if user has update or delete permission
  if (hasAccess(resource, 'update') || hasAccess(resource, 'delete')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: () => t('actionsLabel'),
      cell: ({ row }) => {
        const data = row.original;
        // Hide edit for contract periods with interval = 0
        const canEdit = isContractPeriod ? data.interval !== 0 : true;
        return (
          <div className="flex gap-0">
            {hasAccess(resource, 'update') && canEdit && (
              isContractPeriod ? (
                <ContractPeriodAction id={data.id} />
              ) : isMatterPracticeArea ? (
                <MatterPracticeAreaAction id={data.id} />
              ) : isTaskType ? (
                <TaskTypeAction id={data.id} />
              ) : (
                <SettingsAction
                  id={data.id}
                  title={title}
                  apiEndpoint={apiEndpoint}
                  queryKey={queryKey}
                />
              )
            )}
            {hasAccess(resource, 'delete') && (
              <DeleteDialog
                id={data.id}
                title={data.name}
                url={apiEndpoint}
                queryKey={queryKey}
              />
            )}
          </div>
        );
      },
    });
  }

  return columns;
}
